import { Feature, isAvailable } from 'App/services/FeatureHelperStandalone';
import AppService from 'App/legacy/AppService';

export const hasBulkFeature = () => {
	return (
		isAvailable(Feature.PROSPECTING_BULK) ||
		(isAvailable(Feature.PROSPECTING_BULK_CREDIT) && !!AppService.getMetadata()?.params?.ProspectingBulkCredit)
	);
};

export const isCreditBulk = () => {
	return (
		!isAvailable(Feature.PROSPECTING_BULK) &&
		isAvailable(Feature.PROSPECTING_BULK_CREDIT) &&
		!!AppService.getMetadata()?.params?.ProspectingBulkCredit
	);
};

/** A pay-per-company account adding more new companies than it has credits left for. */
export const lacksCredits = (companiesToAdd: number) =>
	isCreditBulk() && companiesToAdd > (AppService.getMetadata()?.credits?.bisnode ?? 0);

export const CountryConfig = {
	// Noridc
	SE: {
		industryField: 'sniCode',
		companyFormField: 'companyForm',
		currency: 'SEK',
		supportKeywords: true,
		supportBranches: true,
		orgNumberPattern: /^\d{6}-?\d{4}$/,
		legalEntities: [
			['ab', 'aktiebolag', 'aktiebolaget'],
			['hb', 'handelsbolag', 'handelsbolaget'],
			['kb', 'kommanditbolag', 'kommanditbolaget']
		]
	},
	NO: {
		industryField: 'naceCode',
		companyFormField: 'companyForm',
		currency: 'NOK',
		supportKeywords: false,
		supportBranches: true,
		orgNumberPattern: /^(?:\d{9}|\d{3} \d{3} \d{3})$/,
		legalEntities: ['asa', 'as']
	},
	DK: {
		industryField: 'naceCode',
		companyFormField: 'companyForm',
		currency: 'DKK',
		supportKeywords: false,
		supportBranches: true,
		orgNumberPattern: /^\d{8}$/,
		legalEntities: ['is', 'aps', 'ks', 'ps']
	},
	FI: {
		industryField: 'naceCode',
		companyFormField: 'companyForm',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: false,
		orgNumberPattern: /^\d{7}-?\d{1}$/,
		legalEntities: [
			['oy', 'osakeyhtiö'],
			['oy', 'osuuskunta'],
			['ky', 'kommandiittiyhtiö']
		]
	},
	// Rest of europe
	GB: {
		industryField: 'ukSicCode',
		companyFormField: 'companyFormRaw',
		currency: 'GBP',
		supportKeywords: false,
		supportBranches: false,
		orgNumberPattern: /^[A-Z]{2}[0-9]{6}$|[0-9]{8}/,
		legalEntities: []
	},
	BE: {
		industryField: 'naceCode',
		companyFormField: 'companyFormRaw',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: false,
		orgNumberPattern: /^\d{9,10}$/,
		legalEntities: []
	},
	FR: {
		industryField: 'naceCode',
		companyFormField: 'companyFormRaw',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: true,
		orgNumberPattern: /^\d{9}$/,
		legalEntities: []
	},
	IE: {
		industryField: 'naceCode',
		companyFormField: 'companyFormRaw',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: false,
		orgNumberPattern: /^IE\d{6}$/,
		legalEntities: []
	},
	IT: {
		industryField: 'naceCode',
		companyFormField: 'companyFormRaw',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: true,
		orgNumberPattern: /^\d{11}$/,
		legalEntities: []
	},
	LU: {
		industryField: 'naceCode',
		companyFormField: 'companyFormRaw',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: false,
		orgNumberPattern: /^B\d{6}$/,
		legalEntities: []
	},
	NL: {
		industryField: 'naceCode',
		companyFormField: 'companyFormRaw',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: true,
		orgNumberPattern: /^\d{8}$/,
		legalEntities: []
	},
	DE: {
		industryField: 'naceCode',
		companyFormField: 'companyFormRaw',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: false,
		orgNumberPattern: /^\d{9}$|^(VR|HRA|HRB)\s?[a-zA-Z0-9_]*$/,
		legalEntities: ['gmbh', 'ohg', 'ag', 'kg']
	},
	AT: {
		industryField: 'naceCode',
		companyFormField: 'companyFormRaw',
		currency: 'EUR',
		supportKeywords: false,
		supportBranches: false,
		orgNumberPattern: /^FN [A-Z0-9]{8}$/,
		legalEntities: []
	}
} as const;

export type Country = keyof typeof CountryConfig;

export const Countries = Object.keys(CountryConfig) as Country[];

export enum StatusCategory {
	InactiveOrDeregistered,
	ActiveWithWarning,
	Active
}

export function getAvailableCountries(): Country[] {
	const countries: Country[] = [];
	Countries.forEach(country => {
		if (isAvailable(Feature[`PROSPECTING_${country}`])) {
			countries.push(country);
		}
	});

	return countries;
}

export const getAllowedCountries = () => {
	// Get the allowed prospecting countries from the user params, based
	// on the countries an user is allowed to prospect in.
	let allowedCountries: Country[] = getAvailableCountries();
	const { userParams } = AppService.getSelf();
	if (userParams) {
		const { allowedProspectingCountries } = userParams;
		if (allowedProspectingCountries) {
			if (allowedProspectingCountries === 'all') {
				return allowedCountries;
			} else {
				const allowedCountriesArray = allowedProspectingCountries.split(',') as Country[];
				allowedCountries = allowedCountries.filter(country => allowedCountriesArray.includes(country));
			}
		}
	}

	return allowedCountries;
};

export function getCountryFromProspectingId(prospectingId: string | null | undefined): Country | null {
	if (typeof prospectingId !== 'string') {
		return null;
	}

	if (prospectingId.startsWith('UK')) {
		return 'GB';
	}

	return (
		TypedObject.keys(CountryConfig).find(country => {
			return prospectingId.startsWith(country);
		}) || null
	);
}

export function getAvailableCountryFromProspectingId(prospectingId: string): Country | null {
	const country = getCountryFromProspectingId(prospectingId);

	if (country === null) {
		return null;
	}

	const countries = getAvailableCountries();
	return countries.includes(country) ? country : null;
}

export function getIsHeadquarterFromProspectingId(prospectingId: string | null | undefined): boolean | null {
	if (typeof prospectingId !== 'string') {
		return null;
	}
	const [, headquarters] = prospectingId.split('.');
	return headquarters === '1';
}

export function getMonitorAvailableFromProspectingId(prospectingId: string | null | undefined): boolean | null {
	const country = getCountryFromProspectingId(prospectingId);

	if (country === null) {
		return null;
	}

	switch (country) {
		case 'SE':
			return isAvailable(Feature.PROSPECTING_SE);
		default:
			return isAvailable(Feature[`PROSPECTING_${country}`]);
	}
}

export function getDefaultCompanyFormFieldFromCountry(country: Country) {
	return (CountryConfig[country] || CountryConfig['SE']).companyFormField;
}

export function getDefaultCompanyFormFieldFromProspectingId(prospectingId: string) {
	const country = getCountryFromProspectingId(prospectingId);
	return getDefaultCompanyFormFieldFromCountry(country ?? 'SE');
}

export function getDefaultIndustryFieldFromCountry(country: Country) {
	return (CountryConfig[country] || CountryConfig['SE']).industryField;
}

export function getDefaultIndustryFieldFromProspectingId(prospectingId: string) {
	const country = getCountryFromProspectingId(prospectingId);
	return getDefaultIndustryFieldFromCountry(country ?? 'SE');
}

export function getDefaultCurrencyFromCountry(country: Country) {
	return (CountryConfig[country] || CountryConfig['SE']).currency;
}

export function getDefaultCurrencyFromProspectingId(prospectingId: string) {
	const country = getCountryFromProspectingId(prospectingId);
	return getDefaultCurrencyFromCountry(country ?? 'SE');
}

export function validateOrgNumberForCountry(country: Country, orgNumber: string) {
	return (CountryConfig[country] || CountryConfig['SE']).orgNumberPattern.test(orgNumber);
}

export type SimilarCompaniesAnchor = { orgNo: string; country: Country };

/**
 * What a "find similar" search anchors on: the company's org number in the form
 * the register uses, and the prospecting market it belongs to. The market comes
 * from the prospecting id when the company has one, else from the first address
 * in a market the customer can prospect in.
 *
 * Null when the company cannot be anchored - no org number, no market, or an org
 * number that is not valid for the market - which is when the company card
 * offers a refresh instead of a search. Shared by that card and the workspace
 * seed it opens (WEB-11024), so the two cannot disagree on what can be searched.
 */
export function getSimilarCompaniesAnchor(client: {
	orgNo?: string | null;
	prospectingId?: string | null;
	addresses?: Array<{ country: string | null }> | null;
}): SimilarCompaniesAnchor | null {
	if (!client.orgNo) return null;
	const availableCountries = getAvailableCountries();
	const country = client.prospectingId
		? getAvailableCountryFromProspectingId(client.prospectingId)
		: (client.addresses?.find(address => address.country && availableCountries.includes(address.country as Country))
				?.country as Country | undefined) ?? null;
	if (!country) return null;
	const orgNo = client.orgNo.replace(country === 'GB' ? /[^A-Za-z0-9]/g : /\D/g, '');
	return validateOrgNumberForCountry(country, orgNo) ? { orgNo, country } : null;
}

export function isKeywordsSupportedForCountry(country: Country) {
	return CountryConfig[country]?.supportKeywords ?? false;
}

export function isBranchesSupportedForCountry(country: Country) {
	return CountryConfig[country]?.supportBranches ?? false;
}

export function removeLegalEntities(name: string, country?: Country) {
	const legalEntityList = country
		? CountryConfig[country].legalEntities.flat(1)
		: Object.values(CountryConfig)
				.map(({ legalEntities }) => legalEntities)
				.flat(2);
	const legalEntityRegexp = new RegExp(`\\b(${legalEntityList.join('|')})\\b`, 'g');

	return name.replace(legalEntityRegexp, '').trim().replace(/  +/g, ' ');
}
